// src/components/curated/TripAgentChat.jsx
// "Edit with AI" chat. Renders as a side panel (desktop) or a bottom sheet (mobile).
// The page owns the conversation and the trip; this only displays and collects input.
import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Undo2, X, Loader2, Headphones } from "lucide-react";

const ORANGE = "#FF5B15";
const INK = "#0F172A";
const MUTED = "#64748B";
const GREEN = "#059669";
const AMBER = "#B45309";

export default function TripAgentChat({
    variant = "panel",          // panel | sheet
    messages,
    busy,
    suggestions = [],
    options = null,             // [{ group, label, ops }] → options-only mode, no text box
    onPick,
    canUndo,
    onSend,
    onUndo,
    onHuman,
    onClose,
    fill = false,               // panel stretches to its container's height
}) {
    const [text, setText] = useState("");
    const optionsOnly = Array.isArray(options);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, busy]);

    useEffect(() => {
        if (variant !== "sheet") return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [variant, onClose]);

    const send = (value) => {
        const v = String(value ?? text).trim();
        if (!v || busy) return;
        onSend(v);
        setText("");
        inputRef.current?.focus();
    };

    const body = (
        <div style={{ ...S.wrap, ...(fill ? { height: "auto", flex: 1, minHeight: 0 } : null), ...(variant === "sheet" ? S.wrapSheet : null) }} className={variant === "sheet" ? "ta-sheet" : ""} data-leadbot-root="" onClick={(e) => e.stopPropagation()}>
            <div style={S.head}>
                <span style={S.badge}><Sparkles size={15} fill="#fff" color="#fff" /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: INK }}>Edit this trip with AI</div>
                    <div style={{ fontSize: 12, color: MUTED }}>{optionsOnly ? "Pick a change. The price updates instantly." : "Ask in plain words. Every change re-prices instantly."}</div>
                </div>
                {canUndo && (
                    <button onClick={onUndo} style={S.iconBtn} title="Undo last change" aria-label="Undo last change"><Undo2 size={16} /></button>
                )}
                {variant === "sheet" && (
                    <button onClick={onClose} style={S.iconBtn} aria-label="Close"><X size={17} /></button>
                )}
            </div>

            <div ref={listRef} style={S.list} aria-live="polite">
                {messages.map((m) => (
                    <div key={m.id} style={{ display: "flex", justifyContent: m.from === "user" ? "flex-end" : "flex-start" }}>
                        <div style={m.from === "user" ? S.userMsg : S.agentMsg}>
                            {m.text && <div>{m.text}</div>}
                            {m.changes?.length > 0 && (
                                <ul style={S.changes}>
                                    {m.changes.map((c, i) => <li key={i} style={{ color: GREEN }}>✓ <span style={{ color: INK }}>{c}</span></li>)}
                                </ul>
                            )}
                            {m.rejected?.length > 0 && (
                                <ul style={S.changes}>
                                    {m.rejected.map((c, i) => <li key={i} style={{ color: AMBER }}>• <span style={{ color: "#334155" }}>{c}</span></li>)}
                                </ul>
                            )}
                            {m.priceNote && <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>{m.priceNote}</div>}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div style={{ display: "flex" }}>
                        <div style={{ ...S.agentMsg, display: "inline-flex", alignItems: "center", gap: 6, color: MUTED }}>
                            <Loader2 size={14} className="ta-spin" /> Updating your trip
                        </div>
                    </div>
                )}
            </div>

            {optionsOnly ? (
                <div style={S.opts} role="group" aria-label="Changes you can make">
                    {options.map((o) => (
                        <button key={o.label} onClick={() => !busy && onPick?.(o)} disabled={busy} style={{ ...S.sug, whiteSpace: "normal", opacity: busy ? 0.5 : 1 }}>{o.label}</button>
                    ))}
                </div>
            ) : (<>
                {suggestions.length > 0 && (
                    <div style={S.sugs}>
                        {suggestions.map((s) => (
                            <button key={s} onClick={() => send(s)} disabled={busy} style={S.sug}>{s}</button>
                        ))}
                    </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); send(); }} style={S.form}>
                    <input
                        ref={inputRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="e.g. cheaper hotel, add a day, we are 4 from Mumbai"
                        aria-label="Ask the trip assistant"
                        maxLength={300}
                        style={S.input}
                    />
                    <button type="submit" disabled={busy || !text.trim()} style={{ ...S.send, opacity: busy || !text.trim() ? 0.5 : 1 }} aria-label="Send">
                        <Send size={17} />
                    </button>
                </form>
            </>)}
            <button onClick={onHuman} style={S.human}>
                <Headphones size={14} /> Rather talk to a person? Get a callback
            </button>
            <style>{CSS}</style>
        </div>
    );

    if (variant !== "sheet") return body;
    return (
        <div style={S.bg} onClick={onClose}>
            {body}
        </div>
    );
}

const S = {
    wrap: { background: "#fff", border: "1px solid #EEE", borderRadius: 18, display: "flex", flexDirection: "column", height: 470, boxShadow: "0 10px 30px -18px rgba(15,23,42,.3)", overflow: "hidden", fontFamily: "'Inter', -apple-system, system-ui, sans-serif" },
    wrapSheet: { position: "fixed", left: 0, right: 0, bottom: 0, height: "min(78dvh, 640px)", borderRadius: "22px 22px 0 0", border: "none", zIndex: 961 },
    bg: { position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 960 },
    head: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: "1px solid #F1F5F9" },
    badge: { width: 32, height: 32, borderRadius: 10, background: `linear-gradient(135deg, ${ORANGE}, #7C3AED)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    iconBtn: { width: 34, height: 34, borderRadius: 10, border: "1px solid #E2E8F0", background: "#fff", color: INK, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    list: { flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 },
    agentMsg: { maxWidth: "90%", background: "#F8FAFC", border: "1px solid #F1F5F9", color: "#1E293B", fontSize: 13, lineHeight: 1.5, borderRadius: "14px 14px 14px 4px", padding: "9px 12px" },
    userMsg: { maxWidth: "85%", background: ORANGE, color: "#fff", fontSize: 13, lineHeight: 1.45, borderRadius: "14px 14px 4px 14px", padding: "9px 12px" },
    changes: { listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 3, fontSize: 12.5 },
    sugs: { display: "flex", gap: 6, overflowX: "auto", padding: "8px 14px 0", flexShrink: 0 },
    sug: { whiteSpace: "nowrap", border: "1.5px solid #FFD6C2", background: "#FFFAF7", color: "#C2410C", fontSize: 12, fontWeight: 600, padding: "6px 11px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
    opts: { display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 14px 6px", borderTop: "1px solid #F1F5F9", flexShrink: 0 },
    form: { display: "flex", gap: 8, padding: "10px 14px 6px", flexShrink: 0 },
    input: { flex: 1, minWidth: 0, height: 44, border: "1.5px solid #E2E8F0", borderRadius: 12, padding: "0 12px", fontSize: 16, fontFamily: "inherit", color: INK, outline: "none" },
    send: { width: 44, height: 44, borderRadius: 12, border: "none", background: ORANGE, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    human: { flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, border: "none", background: "transparent", color: MUTED, fontSize: 12, fontWeight: 600, padding: "4px 14px 12px", cursor: "pointer", fontFamily: "inherit" },
};

const CSS = `
@keyframes taSpin{to{transform:rotate(360deg)}}
.ta-spin{animation:taSpin 1s linear infinite}
@keyframes taUp{from{transform:translateY(100%)}to{transform:none}}
.ta-sheet{animation:taUp .24s cubic-bezier(.4,0,.2,1);padding-bottom:env(safe-area-inset-bottom)}
@media (prefers-reduced-motion: reduce){ .ta-sheet,.ta-spin{animation:none} }
`;
